import { Tooltip, type TooltipProps } from 'antd';
import React, { useEffect, useRef, useState, type ReactNode } from 'react';

export interface TruncatedTooltipProps extends Omit<TooltipProps, 'visible' | 'onVisibleChange'> {
  /** Il contenuto di testo o nodo da visualizzare/troncare */
  children: ReactNode | string;
  /** Classi da applicare al contenitore del testo */
  className?: string;
  /** Stili da applicare al contenitore del testo */
  style?: React.CSSProperties;
}

const TruncatedTooltip: React.FC<TruncatedTooltipProps> = ({
  children,
  className,
  style,
  title,
  ...tooltipProps
}) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [visible, setVisible] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  // Controlla se il contenuto è troncato, ricalcolando quando l'elemento cambia dimensione
  // (non solo alla resize della finestra, ad es. ridimensionamento di una colonna della tabella).
  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const checkTruncation = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    checkTruncation();
    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(el);
    return () => resizeObserver.disconnect();
  }, [children]);

  // Gestisce l'apertura/chiusura del Tooltip
  const handleVisibleChange = (nextVisible: boolean) => {
    setVisible(nextVisible && isTruncated);
  };

  return (
    <Tooltip
      title={title ?? children}
      open={visible}
      onOpenChange={handleVisibleChange}
      {...tooltipProps}
    >
      <div
        ref={textRef}
        className={className}
        style={{
          ...style,
          // After caller style so table headers cannot lose nowrap (letter-wrap under %).
          minWidth: 0,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

export default TruncatedTooltip;
